import { useEffect, useState } from 'react'
import { Alert } from '@iveco-ti/iveco-ds'
import { useFormContext } from '@iveco-ti/iveco-ds/hook-form'
import { Info, OctagonAlert } from 'lucide-react'
import Cropper, { Area } from 'react-easy-crop'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { getCroppedImg } from '@/common/utils/cropImage'

type FormNewsImageProps = {
  aspectRatio: number
}

export function FormNewsImage({ aspectRatio }: FormNewsImageProps) {
  const [image, setImage] = useState<File | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)

  const { t } = useTranslation('panel')
  const { getValues, ...hookForm } = useFormContext()

  const onCropComplete = async (_: Area, croppedAreaPixels: Area) => {
    if (!croppedAreaPixels?.height || !croppedAreaPixels?.width) return

    try {
      if (!image) {
        throw new Error('Image is invalid')
      }

      const imageUrl = URL.createObjectURL(image)

      if (!imageUrl) {
        throw new Error('Image URL is invalid')
      }

      const croppedImage = await getCroppedImg(
        imageUrl,
        croppedAreaPixels as Area,
      )

      hookForm.setValue('postImage', croppedImage)
    } catch {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: OctagonAlert,
          title: t('news.drawer_create_news.image.error_crop_image'),
        },
      })
    }
  }

  useEffect(() => {
    const image = getValues('image') as File
    setImage(image)

    return () => setImage(null)
  }, [getValues])

  return (
    <div className="h-full flex flex-col gap-6">
      <h2 className="text-2xl text-sky-700 font-semibold">
        {t('news.drawer_create_news.image.title')}
      </h2>

      <div className="relative h-full">
        <Cropper
          image={image ? URL.createObjectURL(image) : undefined}
          crop={crop}
          zoom={zoom}
          aspect={aspectRatio}
          showGrid={false}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
          classes={{
            containerClassName: 'rounded-xl bg-base-600',
            cropAreaClassName: 'rounded-xl !border-brand-500',
          }}
        />
      </div>

      <Alert.Root color="primary">
        <Alert.Icon icon={<Info size={16} />} />

        <Alert.Body>
          <Alert.Description>
            {t('news.drawer_create_news.image.tip')}
          </Alert.Description>
        </Alert.Body>
      </Alert.Root>
    </div>
  )
}
